import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { lensLabel } from "../lib/lenses.js";
import { fmtZ } from "../lib/search.js";

const SPAN = 14; // seconds of trace across the strip
const clamp01 = v => Math.max(0, Math.min(1, v));

function lensRgb(el) {
  let v = getComputedStyle(el).getPropertyValue("--lens").trim().replace("#", "");
  if (v.length === 3) v = v.split("").map(c => c + c).join("");
  const n = parseInt(v, 16) || 0xe9e3d6;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * The activity strip in the top bar: one channel per area, one spike per new item. Busier
 * areas fire faster and areas far above their baseline fire in bursts, so the day's hot spots
 * show at a glance from any lens. Hover names a channel; a click opens that area.
 */
export default function ActivityStrip({ shell, lens, lenses }) {
  const boxRef = useRef(null);
  const cvRef = useRef(null);
  const hoverRef = useRef(-1);
  const chRef = useRef([]);
  const redrawRef = useRef(() => {});
  const [tip, setTip] = useState(null);
  const navigate = useNavigate();
  const D = shell.day;
  const opensIn = ["field", "explore", "today"].find(id => lenses.some(l => l.id === id)) || lens;

  useEffect(() => {
    const cv = cvRef.current;
    const ctx = cv.getContext("2d");
    const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
    let w = 0, h = 0, t = 0, last = 0, raf = 0;
    let C = lensRgb(cv);
    const maxToday = Math.max(1, ...D.areas.map(a => a.today));
    const ch = D.areas.map(a => ({
      a,
      spikes: [],
      burst: 0,
      bt: 0,
      rate: 0.25 + (1.4 * a.today) / maxToday,
      bz: Math.max(0, a.z - 1),
      hot: clamp01((a.z + 0.5) / 4),
    }));
    chRef.current = ch;

    const step = dt => {
      t += dt;
      for (const c of ch) {
        if (c.burst > 0) {
          c.bt -= dt;
          if (c.bt <= 0) {
            c.spikes.push(t);
            c.burst--;
            c.bt = 0.05 + Math.random() * 0.09;
          }
        } else if (Math.random() < c.rate * dt) {
          c.spikes.push(t);
          if (Math.random() < 0.06 * c.bz) {
            c.burst = 3 + ((Math.random() * 5) | 0);
            c.bt = 0.07;
          }
        }
        while (c.spikes.length && t - c.spikes[0] > SPAN) c.spikes.shift();
      }
    };
    for (let k = 0; k < SPAN * 30; k++) step(1 / 30); // start with a full trace

    const draw = () => {
      if (!w || !h) return;
      ctx.clearRect(0, 0, w, h);
      const rh = h / ch.length;
      ch.forEach((c, i) => {
        const y = i * rh;
        if (i === hoverRef.current) {
          ctx.fillStyle = "rgba(255,255,255,.08)";
          ctx.fillRect(0, y, w, rh);
        }
        ctx.fillStyle = "rgba(255,255,255,.045)";
        ctx.fillRect(0, y + rh / 2, w, 0.6);
        for (const s of c.spikes) {
          const age = t - s;
          const alpha = Math.max(0.12, 1 - age / SPAN) * (0.3 + 0.7 * c.hot);
          ctx.fillStyle = `rgba(${C[0]},${C[1]},${C[2]},${alpha.toFixed(3)})`;
          ctx.fillRect(w - (age / SPAN) * w, y + 0.4, 1.4, Math.max(1, rh - 0.8));
        }
      });
      ctx.fillStyle = `rgba(${C[0]},${C[1]},${C[2]},.9)`;
      ctx.fillRect(w - 1.5, 0, 1.5, h);
    };
    const size = () => {
      const r = cv.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      w = r.width;
      h = r.height;
      cv.width = Math.round(w * dpr);
      cv.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      draw();
    };
    const loop = now => {
      const dt = Math.min(0.1, (now - last) / 1000 || 0);
      last = now;
      if (!document.hidden) {
        step(dt);
        draw();
      }
      raf = requestAnimationFrame(loop);
    };
    const ro = new ResizeObserver(size);
    ro.observe(cv);
    if (!reduce) raf = requestAnimationFrame(loop);
    redrawRef.current = () => {
      C = lensRgb(cv);
      draw();
    };
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      redrawRef.current = () => {};
    };
  }, [D]);

  // The trace takes the open lens's accent.
  useEffect(() => redrawRef.current(), [lens]);

  const rowAt = e => {
    const r = cvRef.current.getBoundingClientRect();
    const n = chRef.current.length;
    return Math.max(0, Math.min(n - 1, Math.floor((e.clientY - r.top) / (r.height / n))));
  };
  const top3 = D.areas
    .slice()
    .sort((a, b) => b.z - a.z)
    .slice(0, 3)
    .map(a => `${a.short || a.name} z ${fmtZ(a.z)}`)
    .join(", ");

  return (
    <div
      ref={boxRef}
      className="app-strip"
      role="img"
      aria-label={`Activity: one channel per area, one spike per new item. Most active today: ${top3}.`}
      onPointerMove={e => {
        const i = rowAt(e);
        const a = chRef.current[i]?.a;
        if (!a) return;
        hoverRef.current = i;
        const r = boxRef.current.getBoundingClientRect();
        setTip({ a, x: Math.max(120, Math.min(r.width - 40, e.clientX - r.left)) });
        redrawRef.current();
      }}
      onPointerLeave={() => {
        hoverRef.current = -1;
        setTip(null);
        redrawRef.current();
      }}
      onClick={e => {
        const a = chRef.current[rowAt(e)]?.a;
        if (a) navigate(shell.link(opensIn, a.id));
      }}
    >
      <canvas ref={cvRef} />
      {tip && (
        <div className="app-tip" style={{ left: tip.x }}>
          <b>{tip.a.name}</b> · {tip.a.today} today · z {fmtZ(tip.a.z)} · click for {lensLabel(opensIn)}
        </div>
      )}
    </div>
  );
}
